'use client';
import { useState, useEffect, useCallback } from 'react';
import * as Styled from './styled';
import { IRepositoryEntry, IRepository } from '@/types';

import { IRepositoryBranch, IRepositoryBranchesResponse } from '@/lib/repositoryBranchesApi';
import { ITabularFileResponse, IFileVersion } from '@/lib/tabularFileApi';

import FileSelect from './FileSelect';
// TODO: Replace with BranchSelector Component in order to support commits as well as branches
import BranchSelect from './BranchSelect';
import VersionSelect from './VersionSelect';

import useTabularFileEntries from './useTabularFileEntries';
import useFileVersions from './useFileVersions';
import useRepositoryBranches from './useRepositoryBranches';
import Flash from '@/ui/Flash';

export type BranchFileVersionSelectProps = {
  initialEntry?: IRepositoryEntry;
  repository: IRepository;
  initialBranchesResponse?: IRepositoryBranchesResponse;
  initialBranch?: IRepositoryBranch;
  initialEntriesResponse?: ITabularFileResponse;
  initialVersion?: string;
  onChange: (version: IFileVersion) => void;
  alignment?: 'row' | 'column';
  supressVersion?: boolean;
  storyBookMockEntries?: IRepositoryEntry[];
};

type CommitVersionSelectProps = {
  repository: IRepository;
  selectedBranch?: IRepositoryBranch;
  selectedFilePath?: string | null;
  selectedVersion?: IFileVersion;
  availableVersions: IFileVersion[];
  onChange: (version: IFileVersion | undefined) => void;
  isLoading: boolean;
};

const CommitVersionSelect: React.FC<CommitVersionSelectProps> = ({
  selectedVersion: initialVersion,
  availableVersions,
  isLoading: versionsLoading,
  onChange = (v) => {
    console.log('BranchFileVersionSelect > onChange', v);
  },
}) => {
  const [selectedVersion, setSelectedVersion] = useState<IFileVersion | undefined>(initialVersion);
  useEffect(() => {
    selectedVersion && onChange(selectedVersion);
  }, [selectedVersion, onChange]);

  return (
    <VersionSelect
      selectedVersion={selectedVersion || availableVersions[0]}
      versions={availableVersions}
      onChange={setSelectedVersion}
      placeholder={versionsLoading ? 'Loading...' : 'Select a version...'}
    />
  );
};

const BranchFileVersionSelect: React.FC<BranchFileVersionSelectProps> = ({
  repository,
  initialBranchesResponse,
  initialBranch,
  initialEntry,
  initialEntriesResponse,
  initialVersion,
  supressVersion = false,
  onChange = (v) => {
    console.log('BranchFileVersionSelect > onChange', v);
  },
  storyBookMockEntries,
  alignment = 'column',
}) => {
  const [selectedEntry, setSelectedEntry] = useState<IRepositoryEntry | undefined>(initialEntry);
  const [selectedVersion, setSelectedVersion] = useState<IFileVersion | undefined>(undefined);
  const [selectedBranch, setSelectedBranch] = useState<IRepositoryBranch | undefined>(initialBranch);
  const [availableVersions, setAvailableVersions] = useState<IFileVersion[]>([]);

  const { branches, isLoading: branchesLoading } = useRepositoryBranches({
    repository: repository,
    initialData: initialBranchesResponse,
  });

  const {
    entries,
    isLoading: entriesLoading,
    status: entriesStatus,
  } = useTabularFileEntries({
    repository: repository,
    branch: selectedBranch,
    initialData: initialEntriesResponse,
  });

  // TODO: This really shouldn't call the API if the supressVersion flag is set
  const { versions, isLoading: versionsLoading } = useFileVersions({
    repository: repository,
    branch: selectedBranch,
    filePath: selectedEntry?.resource?.path as string,
  });

  const onVersionsLoaded = useCallback(() => {
    const version = versions.find((version) => version?.commit?.id === initialVersion) || versions[0];
    setAvailableVersions(versions);
    setSelectedVersion(version);
  }, [versions, initialVersion]);

  useEffect(() => {
    versionsLoading ? setSelectedVersion(undefined) : onVersionsLoaded();
  }, [versionsLoading, onVersionsLoaded]);

  useEffect(() => {
    selectedVersion && onChange(selectedVersion);
  }, [selectedVersion, onChange]);

  // console.log('Selected Version', selectedVersion);

  const isStoryBook = !!storyBookMockEntries;

  return (
    <Styled.BranchFileVersionSelect $alignment={alignment}>
      {/* TODO: Replace with BranchSelector Component in order to support commits as well as branches */}
      <BranchSelect
        repository={repository}
        branches={branches}
        selectedBranch={selectedBranch}
        onChange={setSelectedBranch}
        placeholder={branchesLoading ? 'Loading...' : 'Select a branch...'}
      />

      {entriesStatus === 'error' && !isStoryBook ? (
        <Flash.Danger>Error loading file list</Flash.Danger>
      ) : (
        <FileSelect
          entries={entries || storyBookMockEntries}
          selected={selectedEntry}
          onChange={setSelectedEntry}
          placeholder={entriesLoading ? 'Loading...' : 'Select a file...'}
        />
      )}
      {!supressVersion && (
        <CommitVersionSelect
          selectedVersion={selectedVersion}
          repository={repository}
          selectedBranch={selectedBranch}
          selectedFilePath={selectedEntry?.resource?.path}
          availableVersions={availableVersions}
          onChange={setSelectedVersion}
          isLoading={versionsLoading}
        />
      )}
    </Styled.BranchFileVersionSelect>
  );
};

export default BranchFileVersionSelect;
